import { useReducer, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Loader2 } from 'lucide-react';
import type {
  IPhotostudioElement,
  IEditDialogState,
  IEditDialogAction,
} from '@/@types/photostudio';
import type { Id } from '../../../convex/_generated/dataModel';

interface IElementEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  element: IPhotostudioElement | null;
  onSubmit: (
    elementId: Id<'photostudioElements'>,
    name: string,
    description?: string
  ) => Promise<{ success: boolean; error?: string }>;
}

const initialState: IEditDialogState = {
  name: '',
  description: '',
  isSubmitting: false,
  formError: null,
};

function editDialogReducer(state: IEditDialogState, action: IEditDialogAction): IEditDialogState {
  switch (action.type) {
    case 'RESET':
      return {
        ...initialState,
        name: action.payload.name,
        description: action.payload.description,
      };
    case 'SET_NAME':
      return { ...state, name: action.payload };
    case 'SET_DESCRIPTION':
      return { ...state, description: action.payload };
    case 'SET_SUBMITTING':
      return { ...state, isSubmitting: action.payload };
    case 'SET_ERROR':
      return { ...state, formError: action.payload };
    case 'CLEAR':
      return initialState;
  }
}

export function ElementEditDialog({
  open,
  onOpenChange,
  element,
  onSubmit,
}: IElementEditDialogProps) {
  const [state, dispatch] = useReducer(editDialogReducer, initialState);

  const handleClose = useCallback(() => {
    onOpenChange(false);
    dispatch({ type: 'CLEAR' });
  }, [onOpenChange]);

  const handleEdit = useCallback(async () => {
    if (!element || !state.name.trim()) return;
    dispatch({ type: 'SET_SUBMITTING', payload: true });
    const result = await onSubmit(
      element._id,
      state.name.trim(),
      state.description.trim() || undefined
    );
    dispatch({ type: 'SET_SUBMITTING', payload: false });
    if (result.success) {
      handleClose();
    } else {
      dispatch({ type: 'SET_ERROR', payload: result.error || 'Failed to update element' });
    }
  }, [element, state.name, state.description, onSubmit, handleClose]);

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        handleClose();
      } else {
        if (element) {
          dispatch({
            type: 'RESET',
            payload: { name: element.name, description: element.description || '' },
          });
        }
        onOpenChange(isOpen);
      }
    },
    [element, handleClose, onOpenChange]
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Element</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {element?.imageUrl && (
            <div className="aspect-square overflow-hidden rounded-lg border bg-muted">
              <img src={element.imageUrl} alt={element.name} className="size-full object-cover" />
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="edit-name">Name</Label>
            <Input
              id="edit-name"
              value={state.name}
              onChange={e => dispatch({ type: 'SET_NAME', payload: e.target.value })}
              maxLength={100}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-description">Description (optional)</Label>
            <Textarea
              id="edit-description"
              value={state.description}
              onChange={e => dispatch({ type: 'SET_DESCRIPTION', payload: e.target.value })}
              maxLength={500}
              rows={3}
            />
          </div>
          {state.formError && <p className="text-sm text-destructive">{state.formError}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={state.isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleEdit} disabled={!state.name.trim() || state.isSubmitting}>
            {state.isSubmitting && <Loader2 className="mr-2 size-4 animate-spin" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
